import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { ArrowRight } from 'lucide-react';
import { api, parseApiError, type CompetitionStage, type Me, type Role } from '../lib/api';
import { canWrite } from '../lib/roles';
import { hasOrgManagerMembership } from '../lib/orgAccess';

// ─────────────────────────────────────────────────────────────────────────────
// AdvanceStageButton — the org-manager-reachable counterpart to AdminPanel.tsx's
// founder-tier StageControlsSection "Advance" button, for the SAME legacy
// POST /competitions/advance-stage route (null → SEEDING → ROUND_ROBIN →
// PLAYOFFS). No new endpoint, no new gate.
//
// This is the OLD flat single-stage lifecycle — superseded for any comp that
// has composed a real stage pipeline (StagePipelinePanel's CompStage rows,
// driven by startCompStage/endCompStage/confirmDivisions/confirmBracket
// instead). Advancing the legacy `stage` marker alongside an active granular
// pipeline would just confuse two parallel lifecycle models on the same
// competition, so the caller passes `hasStagePipeline` (TournamentSetup
// already computes this via useCompChecklist's `stages` item — no extra
// fetch needed here) and this renders nothing once that's true. A comp that
// has never composed a stage pipeline at all is exactly the legacy case this
// exists for.
//
// Gating mirrors ActivateCompetitionButton EXACTLY: founder/admin OR this
// comp's own org's ORG_ADMIN/ORG_OWNER — widened 2026-07-24 (single
// role-spine rewire, ADMIN can now run comps); retires the ORG_ADMIN-excluded
// OWNER-ONLY carve-out (canActivateCompetitionOrgOwner), which gated
// advance-stage the same as activate/end/create-divisions.
//
// No confirm step (unlike Activate/End) — this mirrors StagePipelineCard's
// own per-stage Start/End buttons, which mutate directly on click; advancing
// one legacy stage is the same class of action as starting/ending one real
// stage, not a comp-wide, hard-to-reverse lock.
// ─────────────────────────────────────────────────────────────────────────────

export function AdvanceStageButton({
  compId,
  stage,
  hasStagePipeline,
  role,
  orgId,
  me,
  onAdvanced,
  className,
}: {
  compId: string;
  /** The comp's legacy flat stage marker (Competition.stage) — null/undefined
   *  means it hasn't started this ladder yet. */
  stage?: CompetitionStage | null;
  /** True once this comp has composed real CompStage rows — hides this
   *  legacy control entirely (see header comment). */
  hasStagePipeline?: boolean;
  role?: Role | null;
  /** The comp's OWN parent org (Competition.orgId) — for the org-owner leg. */
  orgId?: string | null;
  /** effectiveMe — for the org-owner membership check (view-as-aware). */
  me?: Me | null;
  onAdvanced?: () => void;
  className?: string;
}) {
  const [seedingDeadline, setSeedingDeadline] = useState('');
  const [error, setError] = useState('');

  const mut = useMutation({
    mutationFn: () => api.advanceStage(compId, seedingDeadline ? new Date(seedingDeadline).getTime() : undefined),
    onSuccess: () => {
      setError('');
      onAdvanced?.();
    },
    onError: (e: Error) => setError(parseApiError(e).message),
  });

  // Founder/admin, or this comp's own org's ORG_ADMIN/ORG_OWNER — ORG_MEMBER
  // and below never see this control at all, regardless of what else they
  // can view on the surrounding page.
  if (!canWrite(role) && !hasOrgManagerMembership(me, orgId)) return null;
  // The new granular stage pipeline supersedes this legacy ladder entirely —
  // don't offer it once real stages exist.
  if (hasStagePipeline) return null;

  const nextStage: CompetitionStage | null =
    stage == null ? 'SEEDING'
    : stage === 'SEEDING' ? 'ROUND_ROBIN'
    : stage === 'ROUND_ROBIN' ? 'PLAYOFFS'
    : null;
  // Already at the final legacy stage — nothing left to advance to.
  if (!nextStage) return null;

  return (
    <div className={className}>
      <div className="flex flex-wrap items-center gap-2">
        {nextStage === 'SEEDING' && (
          <input
            type="datetime-local"
            value={seedingDeadline}
            onChange={e => setSeedingDeadline(e.target.value)}
            title="Seeding deadline (optional)"
            className="input w-52 text-xs [color-scheme:dark]"
          />
        )}
        <button
          type="button"
          onClick={() => mut.mutate()}
          disabled={mut.isPending}
          className="btn-ghost inline-flex items-center gap-1.5 px-3 py-1.5 text-xs disabled:opacity-50"
        >
          <ArrowRight className="h-3.5 w-3.5" />
          {mut.isPending ? 'Advancing…' : `Advance to ${nextStage.replace(/_/g, ' ').toLowerCase()}`}
        </button>
      </div>
      {error && <p className="mt-1 text-xs text-red-400">{error}</p>}
    </div>
  );
}
